import "./sentry.client";        // ← must be first to capture pageload
import "./lib/i18n/i18n";        // ← initialize i18n before app renders
import "bootstrap-icons/font/bootstrap-icons.css";
import ReactDOM from "react-dom/client"
import App from "./App.tsx"
import "./index.css"
import "../styles/globals.css"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { SalonTzProvider } from "./contexts/SalonTzContext.tsx"
import { SidebarProvider } from "./contexts/SidebarContext.tsx"
import { configureApi } from "./lib/apiClientRuntime.ts"
import { getAuthRegistry } from "./lib/auth/AuthRegistry.ts"
import { initGtmForDemo } from "./gtm.client"

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Focus-refetch stays enabled (default) but only fires when data is
      // older than this — kills the alt-tab refetch storm on all-day salon
      // tabs while keeping instant catch-up on return (doc 07 Phase B).
      staleTime: 30_000,
    },
  },
});

// A demo session survives reloads and in-app navigation for its 8h life, and only the
// FIRST landing goes through SsoPage — so GTM has to be re-armed here too, or everything
// after a refresh goes unmeasured. No-ops for real customers and when the flag is unset.
//
// ⚠ EXCEPT on /sso, where the persisted flag is stale BY DEFINITION: the incoming token is
// the authority on whether this is a demo, and it has not been decoded yet. Arming here
// injected GTM into a real customer's session whenever they demoed first and then signed
// up — the exact path we want people to take — because the flag still said "demo" from the
// earlier visit. SsoPage clears the flag a moment later, but a downloaded script cannot be
// un-downloaded, and `injected` is a one-way latch. Observed on a live signup 2026-09-03.
// SsoPage owns the decision on this route; App.tsx already treats /sso as a special case.
if (window.location.pathname !== "/sso") {
  initGtmForDemo(getAuthRegistry().isDemo());
}

// Restore regional gateway before the app bootstraps so first data calls reuse it.
const savedRegion = getAuthRegistry().getRegion();
if (savedRegion) {
  const regionalBase = `https://api-${savedRegion}.boona.app`;
  configureApi(regionalBase);
  // eslint-disable-next-line no-console
  console.info(`[Bootstrap] API base: ${regionalBase}`);
} else {
  // eslint-disable-next-line no-console
  console.info(`[Bootstrap] No region stored, awaiting SSO login`);
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <SalonTzProvider>
    <QueryClientProvider client={queryClient}>
      <SidebarProvider>
        <App />
      </SidebarProvider>
    </QueryClientProvider>
  </SalonTzProvider>
)
